import {
    Component,
    ViewChild,
    forwardRef,
    OnChanges,
    SimpleChanges,
    ElementRef,
    OnInit, Input
} from '@angular/core';
import { NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MODAL_EDITOR_TOKEN, ModalEditorBase } from '../../../classes/modal-editor-base';
import { GooglePlace } from '../../../models/google-places/google-place.model';
import { GooglePlacesService } from '../../../services/google-places.service';
import { GeoLocation } from 'account-hybrid/common/models/location.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NgSelectComponent } from '@ng-select/ng-select';
import { debounceTime, filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { formatAddress } from '../../../utils/address.util';
import { EntriesPerPlace } from '../../../models/google-places/entries-per-place.model';

@UntilDestroy()
@Component({
    selector: 'ft-places-editor',
    templateUrl: './places-editor.component.html',
    styleUrls: ['places-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PlacesEditorComponent),
        },
        {
            provide: MODAL_EDITOR_TOKEN,
            useExisting: PlacesEditorComponent,
        },
        GooglePlacesService,
    ],
    standalone: false
})
export class PlacesEditorComponent
    extends ModalEditorBase
    implements OnChanges, OnInit
{
    @ViewChild(NgSelectComponent, { static: false })
    selectRef: NgSelectComponent;
    @ViewChild('inputElement', { static: false }) inputElement: ElementRef;
    @ViewChild('selectModel', { static: false }) selectModelRef: NgModel;

    @Input() location: GeoLocation;
    @Input() currentLanguageCode = 'en';
    @Input() showAmount = false;
    @Input() vendorCompanyUuid: string | number;
    @Input() listPlaceholder = 'No items have been added to this list';

    bindValue = 'googlePlaceId';
    bindLabel = 'name';

    placeToAdd: any;
    currentSearchTerm: string;
    searchTerm$ = new Subject<string>();
    placesOptions$: BehaviorSubject<GooglePlace[]> = this.placesService.places$;
    itemsAmountPerPlace$: BehaviorSubject<EntriesPerPlace[]> = this.placesService.entriesPerPlace$;
    loading$: BehaviorSubject<boolean> = this.placesService.loading$;

    constructor(private placesService: GooglePlacesService) {
        super();
    }

    ngOnInit() {
        this.value = [];

        this.searchTerm$
            .pipe(debounceTime(500), untilDestroyed(this))
            .subscribe((term) => {
                this.onPlaceSearch(term);
            });

        this.value$
            .pipe(
                filter(value => value?.length > 0 && this.showAmount),
                debounceTime(200),
                untilDestroyed(this)
            )
            .subscribe(() => {
               this.requestItemsPerPlace();
            });
    }

    ngOnChanges(changes: SimpleChanges) {}

    addItem() {
        if (!this.selectModelRef?.invalid && !!this.placeToAdd) {
            this.value.push(this.placeToAdd);
            this.placeToAdd = null;
            this.requestItemsPerPlace();
        }
    }

    removeItem(index: number) {
        this.value = this.value.filter((_, i) => i !== index);
    }

    isOptionAlreadyAdded(option: GooglePlace): boolean {
        return this.value?.some(
            (value) => value?.googlePlaceId == option.googlePlaceId
        );
    }

    onPlaceSearch(term: string) {
        if (term) {
            this.currentSearchTerm = term;
            this.placesService.searchNearbyPlaces(term, this.location);
        }
    }

    onScrollToEnd() {
        this.placesService.searchNearbyPlaces(
            this.currentSearchTerm,
            this.location
        );
    }

    formatAddress(place: GooglePlace) {
        const currentLanguageData = place?.languageData?.find(
            (item) => item.languageCode === this.currentLanguageCode
        );

        return formatAddress({
            streetAddress: currentLanguageData?.streetAddress,
            city: currentLanguageData?.city,
            countryName: currentLanguageData?.countryName,
            region: currentLanguageData?.region,
            postalCode: null,
            latitude: null,
            longitude: null,
            area: null,
        });
    }

    applyChanges() {
        if (this.isNgModelExist) {
            this.onChangeFn(this.mapOutputValue(this.value));
        }
        this.save.next(
            this.mapOutputValue(this.outputValue?.[this.name]) as any
        );
    }

    mapOutputValue(places: GooglePlace[]): Partial<GooglePlace>[] {
        return places.map((place) => ({ googlePlaceId: place.googlePlaceId, name: place.name, languageData: place.languageData }));
    }

    requestItemsPerPlace() {
        if (!this.showAmount)
            return;
        this.placesService.getEntriesPerPlace(this.value?.map((item: GooglePlace) => item.googlePlaceId), this.vendorCompanyUuid)
    }
}


